import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  // CloseButton,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { ChevronRightIcon, RepeatIcon } from "@chakra-ui/icons";
import PageLayout from "./PageLayout";
import { Api } from "../Api";
import { createToast } from "../utils/toastUtils";
import { formatDate } from "../utils/dateUtils";
import Pagination from "./shared/Pagination";
import InlineAudioPlayer from "./shared/InlineAudioPlayer";
import { useUser } from "../contexts/UserContext";
import { getUserSignature } from "../services/backend";
import { useNavigate } from "react-router-dom";

const ITEMS_PER_PAGE = 25;

const STATE_COLORS = {
  NOT_PROCESSED: "gray",
  PROCESSING: "yellow",
  PROCESSED: "green",
  FAILED: "red",
};

const fmtDuration = (ms) => {
  if (!ms) return "—";
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m`;
  if (m > 0) return `${m}m ${s % 60}s`;
  return `${s}s`;
};

const shortId = (id) => (id ? id.slice(0, 8) + "…" : "—");

// const SIGNATURE_CALLOUT_DISMISS_KEY = "ethosphere.hideSignatureCallout.v1";

const safeParseTranscription = (value) => {
  if (!value || typeof value !== "string") return null;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const toTranscriptText = (transcriptionJsonString) => {
  const arr = safeParseTranscription(transcriptionJsonString);
  if (!arr || arr.length === 0) return null;
  const words = arr
    .filter((e) => e && e.type === "word" && typeof e.text === "string")
    .map((e) => e.text)
    .filter(Boolean);
  return words.length ? words.join(" ") : null;
};

const tryGet = async (path, fallbackPath, canFallback) => {
  try {
    return await Api.get(path);
  } catch (err) {
    const status = err?.response?.status;
    if (!canFallback || !fallbackPath || (status !== 401 && status !== 403 && status !== 404)) {
      throw err;
    }
    return await Api.get(fallbackPath);
  }
};

const tryPost = async (path, fallbackPath, canFallback) => {
  try {
    return await Api.post(path);
  } catch (err) {
    const status = err?.response?.status;
    if (!canFallback || !fallbackPath || (status !== 401 && status !== 403 && status !== 404)) {
      throw err;
    }
    return await Api.post(fallbackPath);
  }
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/* ─── Snippet audio player ─── */
const SnippetPlayer = ({ snippetId, allowAdminFallback }) => {
  const toast = useToast();
  const toastHelper = createToast(toast);

  return (
    <InlineAudioPlayer
      isDisabled={!allowAdminFallback}
      disabledReason="Admin access required"
      onAttemptWhileDisabled={() => toastHelper.error("Audio playback requires admin access")}
      getBlob={async () => {
        try {
          const res = await Api.get(`/admin/audio_snippets/${snippetId}/audio`, {
            responseType: "blob",
          });
          return res.data;
        } catch {
          toastHelper.error("Failed to load audio");
          throw new Error("Failed to load audio");
        }
      }}
    />
  );
};

/* ─── Batch detail modal ─── */
const BatchDetailModal = ({ batch, isOpen, onClose, allowAdminFallback }) => {
  const [snippets, setSnippets] = useState([]);
  const [snippetsLoading, setSnippetsLoading] = useState(false);
  const [snippetsPage, setSnippetsPage] = useState(1);
  const [snippetsTotal, setSnippetsTotal] = useState(0);
  const [isIngesting, setIsIngesting] = useState(false);
  const toast = useToast();
  const toastHelper = createToast(toast);
  const SNIP_PER_PAGE = 20;

  const fetchSnippetsPage = useCallback(
    async (page) => {
      if (!batch) return;
      try {
        const skip = (page - 1) * SNIP_PER_PAGE;
        const res = await tryGet(
          `/audio_batches/${batch.id}/snippets?skip=${skip}&limit=${SNIP_PER_PAGE}`,
          `/admin/audio_batches/${batch.id}/snippets?skip=${skip}&limit=${SNIP_PER_PAGE}`,
          allowAdminFallback
        );
        const items = res.data.items || res.data || [];
        const total = res.data.total_count || items.length;
        return { items, total };
      } catch {
        toastHelper.error("Failed to load snippets");
        return null;
      } finally {
        // no-op
      }
    },
    [batch, allowAdminFallback] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const loadSnippets = useCallback(
    async (page) => {
      if (!batch) return null;
      setSnippetsLoading(true);
      try {
        const result = await fetchSnippetsPage(page);
        if (result) {
          setSnippets(result.items);
          setSnippetsTotal(result.total);
        }
        return result;
      } finally {
        setSnippetsLoading(false);
      }
    },
    [batch, fetchSnippetsPage]
  );

  useEffect(() => {
    if (isOpen && batch) {
      setSnippetsPage(1);
      loadSnippets(1);
    }
  }, [isOpen, batch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOpen && batch) loadSnippets(snippetsPage);
  }, [snippetsPage]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!batch) return null;

  const handleIngest = async () => {
    if (!batch?.id || isIngesting) return;
    setIsIngesting(true);
    try {
      const before = await fetchSnippetsPage(1);
      await tryPost(
        `/audio_batches/${batch.id}/ingest`,
        `/admin/audio_batches/${batch.id}/ingest`,
        allowAdminFallback
      );

      setSnippetsPage(1);

      // Poll briefly for completion (snippets populated/changed).
      let completed = false;
      for (let attempt = 0; attempt < 10; attempt++) {
        const res = await loadSnippets(1);
        const beforeTotal = before?.total ?? 0;
        const afterTotal = res?.total ?? 0;
        const beforeFirstId = before?.items?.[0]?.id;
        const afterFirstId = res?.items?.[0]?.id;

        if (afterTotal > 0 && (afterTotal !== beforeTotal || afterFirstId !== beforeFirstId || beforeTotal === 0)) {
          completed = true;
          break;
        }
        await sleep(1500);
      }

      if (completed) {
        toastHelper.success("Load completed");
      } else {
        toastHelper.info("Loading in progress", "Snippets may take a moment. Try again shortly.");
      }
    } catch {
      toastHelper.error("Failed to load batch");
    } finally {
      setIsIngesting(false);
    }
  };

  const MetaItem = ({ label, value }) => (
    <Box>
      <Text fontSize="xs" color="gray.400" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" mb={0.5}>
        {label}
      </Text>
      <Text fontSize="sm" color="gray.700" fontFamily={typeof value === "string" && value.length > 20 ? "mono" : undefined}>
        {value || "—"}
      </Text>
    </Box>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="xl" boxShadow="xl" maxW="96vw">
        <ModalHeader fontSize="md" fontWeight="semibold" borderBottomWidth="1px" borderBottomColor="gray.100" pb={4} pt={6} px={7}>
          <HStack spacing={3}>
            <Text>Batch</Text>
            <Text fontFamily="mono" fontSize="sm" color="gray.400" fontWeight="normal">
              {batch.id}
            </Text>
            <Badge colorScheme={STATE_COLORS[batch.state] || "gray"} variant="subtle" borderRadius="full" px={2} fontSize="xs">
              {batch.state}
            </Badge>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody px={7} py={6}>
          <VStack spacing={6} align="stretch">
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={5}>
              <MetaItem label="Device" value={batch.device_id} />
              <MetaItem label="Org" value={batch.org_id} />
              <MetaItem label="Duration" value={fmtDuration(batch.total_duration_ms)} />
              <MetaItem label="Chunks" value={batch.total_chunks} />
              <MetaItem label="Sample Rate" value={batch.sample_rate ? `${batch.sample_rate} Hz` : null} />
              <MetaItem label="Encoding" value={batch.audio_encoding} />
              <MetaItem label="Created" value={formatDate(batch.recorded_at || batch.created_at)} />
              <MetaItem label="Snippets" value={snippetsTotal || batch.snippets?.length} />
            </SimpleGrid>

            <Divider />

            <Box>
              <HStack justify="space-between" align="center" mb={4}>
                <Text fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                  Snippets
                </Text>
                <Button
                  size="xs"
                  variant="outline"
                  colorScheme="blue"
                  leftIcon={<RepeatIcon />}
                  onClick={handleIngest}
                  isLoading={isIngesting}
                  loadingText="Loading"
                >
                  Load
                </Button>
              </HStack>
              {snippetsLoading ? (
                <Flex justify="center" py={8}>
                  <Spinner size="md" color="blue.500" />
                </Flex>
              ) : snippets.length === 0 ? (
                <Box py={8} textAlign="center" bg="gray.50" borderRadius="lg" borderWidth="1px" borderColor="gray.200" borderStyle="dashed">
                  <Text fontSize="sm" color="gray.400">
                    No snippets found
                  </Text>
                </Box>
              ) : (
                <VStack spacing={3} align="stretch">
                  <Box overflowX="auto" borderRadius="lg" borderWidth="1px" borderColor="gray.200" bg="white">
                    <Table variant="unstyled" size="md">
                      <Thead>
                        <Tr bg="gray.50" borderBottomWidth="1px" borderBottomColor="gray.200">
                          {["ID", "Chunk", "Timestamp", "Duration", "Transcripts", ""].map((h) => (
                            <Th key={h} py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                              {h}
                            </Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {snippets.map((snip, idx) => {
                          const filteredText = toTranscriptText(snip.transcription_filtered);
                          const rawText = toTranscriptText(snip.transcription_unfiltered);

                          return (
                            <Tr
                              key={snip.id}
                              borderBottomWidth={idx < snippets.length - 1 ? "1px" : "0"}
                              borderBottomColor="gray.100"
                              _hover={{ bg: "gray.50" }}
                              transition="background 0.1s"
                            >
                            <Td py={3} px={4}>
                              <Text fontSize="xs" color="gray.400" fontFamily="mono">
                                {snip.id}
                              </Text>
                            </Td>
                            <Td py={3} px={4}>
                              <Text fontSize="xs" color="gray.600" fontFamily="mono">
                                {snip.chunk_index ?? "—"}
                              </Text>
                            </Td>
                            <Td py={3} px={4}>
                              <Text fontSize="xs" color="gray.500">
                                {snip.recorded_at
                                  ? formatDate(snip.recorded_at)
                                  : snip.start_timestamp_ms
                                    ? new Date(snip.start_timestamp_ms).toLocaleString()
                                    : "—"}
                              </Text>
                            </Td>
                            <Td py={3} px={4}>
                              <Text fontSize="xs" color="gray.500">
                                {snip.end_timestamp_ms && snip.start_timestamp_ms
                                  ? fmtDuration(snip.end_timestamp_ms - snip.start_timestamp_ms)
                                  : "—"}
                              </Text>
                            </Td>
                            <Td py={3} px={4} minW="440px" maxW="720px">
                              {filteredText || rawText ? (
                                <VStack spacing={1.5} align="stretch">
                                  <HStack spacing={2} align="baseline">
                                    <Text fontSize="xs" color="gray.400" fontWeight="semibold" minW="58px">
                                      Filtered
                                    </Text>
                                    {filteredText ? (
                                      <Tooltip label={filteredText} placement="top" hasArrow>
                                        <Text fontSize="sm" color="gray.800" noOfLines={2}>
                                          {filteredText}
                                        </Text>
                                      </Tooltip>
                                    ) : (
                                      <Text fontSize="sm" color="gray.300">
                                        Pending
                                      </Text>
                                    )}
                                  </HStack>
                                  <HStack spacing={2} align="baseline">
                                    <Text fontSize="xs" color="gray.400" fontWeight="semibold" minW="58px">
                                      Raw
                                    </Text>
                                    {rawText ? (
                                      <Tooltip label={rawText} placement="top" hasArrow>
                                        <Text fontSize="xs" color="gray.600" noOfLines={2}>
                                          {rawText}
                                        </Text>
                                      </Tooltip>
                                    ) : (
                                      <Text fontSize="xs" color="gray.300">
                                        Pending
                                      </Text>
                                    )}
                                  </HStack>
                                </VStack>
                              ) : (
                                <Text fontSize="sm" color="gray.300">
                                  Pending processing
                                </Text>
                              )}
                            </Td>
                            <Td py={3} px={4} textAlign="right">
                              <SnippetPlayer snippetId={snip.id} allowAdminFallback={allowAdminFallback} />
                            </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </Box>
                  {snippetsTotal > SNIP_PER_PAGE && (
                    <Flex justify="flex-end">
                      <Pagination
                        currentPage={snippetsPage}
                        totalItems={snippetsTotal}
                        itemsPerPage={SNIP_PER_PAGE}
                        onPageChange={setSnippetsPage}
                        isDisabled={snippetsLoading}
                      />
                    </Flex>
                  )}
                </VStack>
              )}
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const AudioBatchesPage = () => {
  const { isAdmin } = useUser();
  const allowAdminFallback = Boolean(isAdmin);
  const navigate = useNavigate();

  const [filterState, setFilterState] = useState("");
  // Hidden for now (keep for later).
  // const [filterDeviceId, setFilterDeviceId] = useState("");
  // Admin-only user filter/display belongs in the Admin panel.
  // Keeping this here commented out in case we want to re-enable it later.
  // const [filterUserId, setFilterUserId] = useState("");
  // const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const toastHelper = createToast(toast); // eslint-disable-line no-unused-vars
  const [hasSignature, setHasSignature] = useState(null);
  // Non-optional UX: do not allow dismissing this callout.
  // Keeping the old dismiss state commented out in case we revert later.
  // const [hideSignatureCallout, setHideSignatureCallout] = useState(() => {
  //   try {
  //     return window?.localStorage?.getItem(SIGNATURE_CALLOUT_DISMISS_KEY) === "1";
  //   } catch {
  //     return false;
  //   }
  // });

  const skip = (currentPage - 1) * ITEMS_PER_PAGE;

  useEffect(() => {
    let mounted = true;
    getUserSignature()
      .then((sig) => {
        if (!mounted) return;
        setHasSignature(Boolean(sig));
      })
      .catch(() => {
        // If we can't determine, don't nag the user.
        if (!mounted) return;
        setHasSignature(true);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const fetchBatches = useCallback(async () => {
    const params = new URLSearchParams();
    if (filterState) params.append("state", filterState);
    // Hidden for now (keep for later).
    // if (filterDeviceId) params.append("device_id", filterDeviceId);
    // if (allowAdminFallback && filterUserId) params.append("user_id", filterUserId);
    params.append("skip", skip.toString());
    params.append("limit", ITEMS_PER_PAGE.toString());

    const res = await tryGet(
      `/audio_batches?${params.toString()}`,
      `/admin/audio_batches?${params.toString()}`,
      allowAdminFallback
    );
    return res.data;
  }, [filterState, skip, allowAdminFallback]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // useEffect(() => {
  //   if (!allowAdminFallback) return;
  //   let mounted = true;
  //   Api.get("/admin/users")
  //     .then((r) => {
  //       if (!mounted) return;
  //       setUsers(r.data.users || []);
  //     })
  //     .catch(() => {
  //       if (!mounted) return;
  //       setUsers([]);
  //     });
  //   return () => {
  //     mounted = false;
  //   };
  // }, [allowAdminFallback]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    fetchBatches()
      .then((d) => {
        if (!mounted) return;
        setData(d);
      })
      .catch((e) => {
        if (!mounted) return;
        setError(e);
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [fetchBatches]);

  const batches = data?.items || data || [];
  const totalCount = data?.total_count || (Array.isArray(batches) ? batches.length : 0);

  const handleFilterState = (v) => {
    setFilterState(v);
    setCurrentPage(1);
  };
  // Hidden for now (keep for later).
  // const handleFilterDevice = (v) => {
  //   setFilterDeviceId(v);
  //   setCurrentPage(1);
  // };
  // const handleFilterUser = (v) => {
  //   setFilterUserId(v);
  //   setCurrentPage(1);
  // };

  const openDetail = (batch) => {
    setSelectedBatch(batch);
    onOpen();
  };

  // const dismissSignatureCallout = () => {
  //   setHideSignatureCallout(true);
  //   try {
  //     window?.localStorage?.setItem(SIGNATURE_CALLOUT_DISMISS_KEY, "1");
  //   } catch {}
  // };

  return (
    <PageLayout>
      <VStack spacing={5} align="stretch">
        {hasSignature === false && (
          <Alert
            status="info"
            borderRadius="lg"
            bg="blue.50"
            borderWidth="1px"
            borderColor="blue.100"
            alignItems="flex-start"
          >
            <AlertIcon mt={0.5} />
            <Box flex="1">
              <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                Set up your Voice Signature
              </Text>
              <Text fontSize="xs" color="gray.600" mt={0.5}>
                It helps identify you in conversations and improves results.
              </Text>
              <HStack spacing={2} mt={3}>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={() => navigate("/edit-signature")}
                >
                  Set up Voice Signature
                </Button>
                {/* Non-optional: no dismiss option. */}
                {/* <Button size="sm" variant="ghost" onClick={dismissSignatureCallout}>
                  Not now
                </Button> */}
              </HStack>
            </Box>
            {/* Non-optional: no close button. */}
            {/* <CloseButton onClick={dismissSignatureCallout} /> */}
          </Alert>
        )}

        <Box>
          <Text fontSize="lg" fontWeight="semibold" color="gray.800">
            Audio Batches
          </Text>
          <Text fontSize="sm" color="gray.500">
            View audio batches associated with your account
          </Text>
        </Box>

        <Flex gap={4} wrap="wrap" align="flex-end">
          {/* Hidden for now (keep for later). */}
          {/* <FormControl maxW="280px">
            <FormLabel fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" mb={1}>
              Device ID
            </FormLabel>
            <Input
              size="sm"
              value={filterDeviceId}
              onChange={(e) => handleFilterDevice(e.target.value)}
              placeholder="Filter by device UUID"
              fontFamily="mono"
              borderRadius="md"
              bg="white"
            />
          </FormControl> */}
          <FormControl maxW="180px">
            <FormLabel fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" mb={1}>
              State
            </FormLabel>
            <Select
              size="sm"
              value={filterState}
              onChange={(e) => handleFilterState(e.target.value)}
              placeholder="All states"
              borderRadius="md"
              bg="white"
            >
              <option value="NOT_PROCESSED">Not Processed</option>
              <option value="PROCESSING">Processing</option>
              <option value="PROCESSED">Processed</option>
              <option value="FAILED">Failed</option>
            </Select>
          </FormControl>
          {/* Admin-only user filter lives in Admin panel (kept for later). */}
          {/* {allowAdminFallback && (
            <FormControl maxW="320px">
              <FormLabel fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" mb={1}>
                User
              </FormLabel>
              <Select
                size="sm"
                value={filterUserId}
                onChange={(e) => handleFilterUser(e.target.value)}
                placeholder="All users"
                borderRadius="md"
                bg="white"
              >
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.email}
                  </option>
                ))}
              </Select>
            </FormControl>
          )} */}
        </Flex>

        {loading ? (
          <Box display="flex" justifyContent="center" py={10}>
            <Spinner size="lg" color="blue.500" />
          </Box>
        ) : error ? (
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            {error?.message || "Failed to load audio batches"}
          </Alert>
        ) : batches.length === 0 ? (
          <Box py={16} textAlign="center" bg="gray.50" borderRadius="lg" borderWidth="1px" borderColor="gray.200" borderStyle="dashed">
            <Text color="gray.400" fontSize="sm">
              No audio batches found
            </Text>
          </Box>
        ) : (
          <>
            <Box overflowX="auto" borderRadius="lg" borderWidth="1px" borderColor="gray.200" bg="white">
              <Table variant="unstyled" size="sm">
                <Thead>
                  <Tr bg="gray.50" borderBottomWidth="1px" borderBottomColor="gray.200">
                    {[
                      "Batch ID",
                      // ...(allowAdminFallback ? ["User"] : []),
                      "Device",
                      "State",
                      "Duration",
                      "Chunks",
                      // "Snippets", // redundant (snippets shown in batch detail)
                      "Created",
                      "",
                    ].map((h) => (
                      <Th key={h} py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                        {h}
                      </Th>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  {batches.map((batch, idx) => (
                    <Tr
                      key={batch.id}
                      borderBottomWidth={idx < batches.length - 1 ? "1px" : "0"}
                      borderBottomColor="gray.100"
                      _hover={{ bg: "gray.50" }}
                      transition="background 0.1s"
                      cursor="pointer"
                      onClick={() => openDetail(batch)}
                    >
                      <Td py={3} px={4}>
                        <Tooltip label={batch.id} placement="top" hasArrow>
                          <Text fontSize="xs" color="gray.600" fontFamily="mono" cursor="default">
                            {shortId(batch.id)}
                          </Text>
                        </Tooltip>
                      </Td>
                      {/* Admin-only user column lives in Admin panel (kept for later). */}
                      {/* {allowAdminFallback && (
                        <Td py={3} px={4} maxW="220px">
                          <Text fontSize="xs" color="gray.600" noOfLines={1}>
                            {batch.user_email || batch.user?.email || batch.userEmail || batch.owner_email || "—"}
                          </Text>
                        </Td>
                      )} */}
                      <Td py={3} px={4}>
                        <Tooltip label={batch.device_id} placement="top" hasArrow>
                          <Text fontSize="xs" color="gray.600" fontFamily="mono">
                            {shortId(batch.device_id)}
                          </Text>
                        </Tooltip>
                      </Td>
                      <Td py={3} px={4}>
                        <Badge colorScheme={STATE_COLORS[batch.state] || "gray"} variant="subtle" borderRadius="full" px={2} fontSize="xs">
                          {batch.state?.replace("_", " ")}
                        </Badge>
                      </Td>
                      <Td py={3} px={4}>
                        <Text fontSize="xs" color="gray.600">
                          {fmtDuration(batch.total_duration_ms)}
                        </Text>
                      </Td>
                      <Td py={3} px={4}>
                        <Text fontSize="xs" color="gray.600">
                          {batch.total_chunks ?? "—"}
                        </Text>
                      </Td>
                      {/* Redundant; snippets are shown in batch detail modal. */}
                      {/* <Td py={3} px={4}>
                        <Text fontSize="xs" color="gray.600">
                          {batch.snippets?.length ?? "—"}
                        </Text>
                      </Td> */}
                      <Td py={3} px={4}>
                        <Text fontSize="xs" color="gray.400">
                          {formatDate(batch.recorded_at || batch.created_at)}
                        </Text>
                      </Td>
                      <Td py={3} px={4} textAlign="right">
                        <ChevronRightIcon color="gray.300" />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>

            {data?.total_count ? (
              <Flex justify="space-between" align="center">
                <Text fontSize="xs" color="gray.400">
                  Showing {skip + 1}–{Math.min(skip + ITEMS_PER_PAGE, totalCount)} of {totalCount} batches
                </Text>
                <Pagination
                  currentPage={currentPage}
                  totalItems={totalCount}
                  itemsPerPage={ITEMS_PER_PAGE}
                  onPageChange={setCurrentPage}
                  isDisabled={loading}
                />
              </Flex>
            ) : (
              <Flex justify="flex-end">
                <Button size="sm" variant="ghost" isDisabled>
                  Pagination unavailable
                </Button>
              </Flex>
            )}
          </>
        )}
      </VStack>

      <BatchDetailModal
        batch={selectedBatch}
        isOpen={isOpen}
        onClose={onClose}
        allowAdminFallback={allowAdminFallback}
      />
    </PageLayout>
  );
};

export default AudioBatchesPage;

